import { Caption, Icon, TooltipIconButton } from '@if/mui'
import { Divider, Stack } from '@mui/material'
import {
  ArrowLeftRight,
  Bell,
  BellOff,
  CalendarCheck,
  CalendarPlus,
  LucideIcon,
  MessageSquarePlus,
  Plus,
  Repeat,
  Split,
  TrendingDown,
  TrendingUp,
} from 'lucide-react'
import { memo, useMemo } from 'react'
import { useTranslation } from 'react-i18next'
import { BatchUpdatePaymentsProps } from './LoanBatchUpdatePayments/LoanBatchUpdatePaymentsDialog-hook'
import { EEditPaymentsSubAction } from './EditPaymentsDialog'

interface EditPaymentsToolbarProps {
  /** Runs one tool over the current scope. The batch tools say which of the three they are. */
  onTool: (tool: EEditPaymentsSubAction, batchUpdate?: BatchUpdatePaymentsProps) => void
  onSmsReminderBatchChange: (checked: boolean) => void
  canSpreadOverdueAmount: boolean
  hasAmountNotReplaced: boolean
  disabled?: boolean
}

interface ToolSpec {
  key: string
  icon: LucideIcon
  label: string
  onClick: () => void
  disabled?: boolean
}

interface ToolGroupSpec {
  key: string
  label: string
  tools: ToolSpec[]
}

const EditPaymentsToolbar = ({
  onTool,
  onSmsReminderBatchChange,
  canSpreadOverdueAmount,
  hasAmountNotReplaced,
  disabled = false,
}: EditPaymentsToolbarProps) => {
  const { t } = useTranslation()

  const groups = useMemo<ToolGroupSpec[]>(
    () => [
      {
        key: 'schedule',
        label: t('loan.payment.editPayments.tools.schedule'),
        tools: [
          {
            key: 'addPayment',
            icon: Plus,
            label: t('loan.payment.add'),
            onClick: () => onTool(EEditPaymentsSubAction.AddPayment),
          },
          {
            key: 'movePayments',
            icon: ArrowLeftRight,
            label: t('loan.payment.moveSelectedPayments.title'),
            onClick: () => onTool(EEditPaymentsSubAction.MovePayments),
          },
          {
            key: 'addRecurringPayments',
            icon: Repeat,
            label: t('loan.payment.addRecurringPayments.title'),
            onClick: () => onTool(EEditPaymentsSubAction.AddRecurringPayments),
          },
        ],
      },
      {
        key: 'amount',
        label: t('loan.payment.editPayments.tools.amount'),
        tools: [
          {
            key: 'increase',
            icon: TrendingUp,
            label: t('loan.payment.increase'),
            onClick: () =>
              onTool(EEditPaymentsSubAction.BatchUpdatePayments, {
                isCatchUpMode: false,
                isIncreasingPayments: true,
              }),
          },
          {
            key: 'reduce',
            icon: TrendingDown,
            label: t('loan.payment.reduce'),
            onClick: () =>
              onTool(EEditPaymentsSubAction.BatchUpdatePayments, {
                isCatchUpMode: false,
                isIncreasingPayments: false,
              }),
          },
        ],
      },
      {
        key: 'notes',
        label: t('loan.payment.editPayments.tools.notes'),
        tools: [
          {
            key: 'addNote',
            icon: MessageSquarePlus,
            label: t('loan.payment.options.addNote'),
            onClick: () => onTool(EEditPaymentsSubAction.AddNoteToAllPayments),
          },
          {
            key: 'checkAllSms',
            icon: Bell,
            label: t('loan.payment.editPayments.checkAllSMS'),
            onClick: () => onSmsReminderBatchChange(true),
          },
          {
            key: 'uncheckAllSms',
            icon: BellOff,
            label: t('loan.payment.editPayments.uncheckAllSMS'),
            onClick: () => onSmsReminderBatchChange(false),
          },
        ],
      },
      {
        key: 'catchUp',
        label: t('loan.payment.editPayments.tools.catchUp'),
        tools: [
          {
            key: 'catchUpOnSinglePayment',
            icon: CalendarPlus,
            label: t('enum.ECatchUpLatePaymentMethod.catchUpOnSinglePayment'),
            onClick: () => onTool(EEditPaymentsSubAction.CatchUpAmountOfOverdueForDate),
            disabled: !hasAmountNotReplaced,
          },
          {
            key: 'catchUpOnRemainingPayments',
            icon: CalendarCheck,
            label: t('enum.ECatchUpLatePaymentMethod.catchUpOnRemainingPayments'),
            onClick: () =>
              onTool(EEditPaymentsSubAction.BatchUpdatePayments, {
                isCatchUpMode: true,
                isIncreasingPayments: true,
              }),
            disabled: !hasAmountNotReplaced,
          },
          {
            key: 'catchUpOnNextPayments',
            icon: Split,
            label: t('enum.ECatchUpLatePaymentMethod.catchUpOnNextPayments'),
            onClick: () => onTool(EEditPaymentsSubAction.SpreadLateAmount),
            disabled: !hasAmountNotReplaced || !canSpreadOverdueAmount,
          },
        ],
      },
    ],
    [canSpreadOverdueAmount, hasAmountNotReplaced, onSmsReminderBatchChange, onTool, t],
  )

  return (
    <Stack
      direction="row"
      spacing={2}
      divider={<Divider orientation="vertical" flexItem />}
      sx={{ flexWrap: 'wrap', rowGap: 1 }}
      useFlexGap
    >
      {groups.map((group) => (
        <Stack key={group.key} spacing={0.5}>
          <Caption>{group.label}</Caption>
          <Stack direction="row" spacing={0.5}>
            {group.tools.map((tool) => (
              <TooltipIconButton
                key={tool.key}
                title={tool.label}
                onClick={tool.onClick}
                disabled={disabled || tool.disabled}
                size="small"
              >
                <Icon icon={tool.icon} size="sm" />
              </TooltipIconButton>
            ))}
          </Stack>
        </Stack>
      ))}
    </Stack>
  )
}

export default memo(EditPaymentsToolbar)
